// ActiveEngagementsWidget — Advice-firm rail listing the most recently
// touched clients with quick context (segment, last update). Click-through
// jumps straight to the client overview. Renders null for non-advice orgs.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Users, ArrowRight, Loader2, ChevronRight } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

function relative(dateStr) {
  if (!dateStr) return null;
  try {
    return formatDistanceToNow(parseISO(dateStr), { addSuffix: true });
  } catch (_) {
    return null;
  }
}

export default function ActiveEngagementsWidget({ ctx }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdvice = ctx?.orgType === 'advice';

  useEffect(() => {
    if (!token || !isAdvice) { setLoading(false); return; }
    (async () => {
      try {
        const res = await api.getClients(token);
        const items = (res.data || [])
          .filter((c) => c.status !== 'archived' && c.status !== 'inactive')
          .sort((a, b) => {
            const aDate = new Date(a.lastActivityAt || a.updatedAt || 0).getTime();
            const bDate = new Date(b.lastActivityAt || b.updatedAt || 0).getTime();
            return bDate - aDate;
          })
          .slice(0, 6);
        setClients(items);
      } catch (e) {
        console.error('ActiveEngagementsWidget fetch failed', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, isAdvice]);

  if (!isAdvice) return null;

  if (loading) {
    return (
      <div className="rounded-xl border border-[#E6E0D9] bg-white p-5 flex items-center justify-center" data-testid="active-engagements-widget-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#4A3B32]" />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white p-5" data-testid="active-engagements-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#4A3B32]" />
          <h3 className="font-heading text-base text-[#2C2420]">Active engagements</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => ctx?.setActiveTab?.('clients')}
          className="h-7 text-xs text-[#A86A3A]"
          data-testid="active-engagements-all-btn"
        >
          All clients <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>

      {clients.length === 0 ? (
        <div className="text-center py-8" data-testid="active-engagements-empty">
          <Users className="w-8 h-8 text-[#D4CCC6] mx-auto mb-2" />
          <p className="text-sm text-[#5D524D] mb-3">No active clients yet.</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => ctx?.setShowClientIntake?.(true)}
            className="border-[#E6E0D9]"
          >
            Start a new intake
          </Button>
        </div>
      ) : (
        <ul className="divide-y divide-[#F0EAE3]">
          {clients.map((c) => {
            const rel = relative(c.lastActivityAt || c.updatedAt);
            return (
              <li key={c.id}>
                <button
                  onClick={() => navigate(`/app/clients/${c.id}/overview`)}
                  className="w-full py-2.5 flex items-center justify-between gap-3 group hover:bg-[#FCFAF7] -mx-2 px-2 rounded transition-colors"
                  data-testid={`active-engagement-${c.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-[#4A3B32] text-white text-xs font-medium flex items-center justify-center shrink-0">
                      {(c.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 text-left">
                      <p className="text-sm font-medium text-[#2C2420] truncate group-hover:text-[#4A3B32]">{c.name}</p>
                      <div className="flex items-center gap-2 text-[11px] text-[#8C8078]">
                        {c.segment && <span>{c.segment}</span>}
                        {c.segment && rel && <span>·</span>}
                        {rel && <span>Updated {rel}</span>}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#8C8078] group-hover:text-[#4A3B32] shrink-0" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
